import { useState, type ImgHTMLAttributes, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { transformedImage, buildSrcSet } from "@/lib/image-url";

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  src: string;
  alt: string;
  widths?: number[];
  sizes?: string;
  fallbackWidth?: number;
  quality?: number;
  resize?: "cover" | "contain" | "fill";
  /** When true, eager-load + high priority (e.g. above-the-fold LCP). */
  priority?: boolean;
  /** Show a tiny upscaled blur placeholder behind the image until it loads. Default true. */
  blurUp?: boolean;
};

export function ResponsiveImage({
  src,
  alt,
  widths = [320, 480, 662, 800, 1200, 1600],
  sizes = "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 662px",
  fallbackWidth = 800,
  quality = 75,
  resize = "cover",
  priority = false,
  blurUp = true,
  className,
  style,
  onLoad,
  ...rest
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const fallback = transformedImage(src, { width: fallbackWidth, quality, resize });
  const srcSet = buildSrcSet(src, widths, { quality, resize });
  // Tiny placeholder used as background; upscaling produces a soft blur.
  const tiny = blurUp ? transformedImage(src, { width: 24, quality: 30, resize }) : undefined;

  const mergedStyle: CSSProperties = {
    ...(tiny && !loaded
      ? {
          backgroundImage: `url("${tiny}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "var(--muted, #eee)",
        }
      : null),
    ...style,
  };

  return (
    <img
      {...rest}
      src={fallback}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "low"}
      onLoad={(e) => { setLoaded(true); onLoad?.(e); }}
      style={mergedStyle}
      className={cn(
        "transition-opacity duration-500 motion-reduce:transition-none",
        loaded || tiny ? "opacity-100" : "opacity-0",
        className,
      )}
    />
  );
}
